/**
 * Notification Defaults Tab Component
 * Manages default notification preferences for new users and system alert emails.
 */
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Textarea } from '../../ui/textarea';
import { Bell } from 'lucide-react';
import { PlatformSettings } from './types';

const NOTIFICATION_TYPES = [
  { key: 'po_created', label: 'PO Created' },
  { key: 'po_acknowledged', label: 'PO Acknowledged' },
  { key: 'po_line_accepted', label: 'PO Line Accepted' },
  { key: 'po_line_declined', label: 'PO Line Declined' },
  { key: 'po_line_change_proposed', label: 'Change Proposed' },
  { key: 'grn_created', label: 'GRN Created' },
  { key: 'grn_disputed', label: 'GRN Disputed' },
  { key: 'grn_resolved', label: 'GRN Resolved' },
  { key: 'asn_submitted', label: 'ASN Submitted' },
  { key: 'asn_delivered', label: 'ASN Delivered' },
  { key: 'ncr_created', label: 'NCR Created' },
  { key: 'ncr_status_changed', label: 'NCR Status Changed' },
  { key: 'document_uploaded', label: 'Document Uploaded' },
  { key: 'document_expiring', label: 'Document Expiring' },
  { key: 'user_registered', label: 'User Registered' },
  { key: 'user_approved', label: 'User Approved' },
  { key: 'delegation_started', label: 'Delegation Started' },
  { key: 'delegation_ended', label: 'Delegation Ended' },
];

interface NotificationDefaultsTabProps {
  platformSettings: PlatformSettings;
  saving: boolean;
  onPlatformSettingsChange: (settings: PlatformSettings) => void;
  onSave: () => void;
}

export function NotificationDefaultsTab({
  platformSettings,
  saving,
  onPlatformSettingsChange,
  onSave,
}: NotificationDefaultsTabProps) {
  const updatePlatform = (updates: Partial<PlatformSettings>) => {
    onPlatformSettingsChange({ ...platformSettings, ...updates });
  };

  const updateNotificationPreference = (key: string, value: boolean) => {
    updatePlatform({
      defaultNotificationPreferences: {
        ...platformSettings.defaultNotificationPreferences,
        [key]: value,
      },
    });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            User Notification Defaults
          </CardTitle>
          <CardDescription>
            Configure default notification preferences for new users and system alert emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-base font-medium mb-3 block">
              Default Notification Preferences
            </Label>
            <p className="text-sm text-muted-foreground mb-4">
              These defaults will be applied when new users are created. Users can override individually.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {NOTIFICATION_TYPES.map((nt) => (
                <div key={nt.key} className="flex items-center justify-between p-3 border rounded-lg">
                  <Label className="text-sm">{nt.label}</Label>
                  <Switch
                    checked={platformSettings.defaultNotificationPreferences[nt.key] ?? true}
                    onCheckedChange={(checked) => updateNotificationPreference(nt.key, checked)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="systemAlertEmails">System Alert Emails</Label>
            <Textarea
              id="systemAlertEmails"
              value={platformSettings.systemAlertEmails}
              onChange={(e) => updatePlatform({ systemAlertEmails: e.target.value })}
              placeholder="admin@company.com, alerts@company.com"
              rows={3}
            />
            <p className="text-sm text-muted-foreground">
              Comma-separated email addresses that will receive system-wide alerts
            </p>
          </div>

          <Button onClick={onSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Notification Settings'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
